import { useState, useEffect } from "react";
import { Database, Plus, PencilSimple, Trash, Check, X, Tag } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMasterData() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingNew, setAddingNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", description: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [deletingId, setDeletingId] = useState(null);
  const [savingNew, setSavingNew] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [error, setError] = useState("");

  const inputCls =
    "w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = {
    borderColor: "rgba(26,92,55,0.2)",
    backgroundColor: "#F7F5F0",
    color: "#1C211F",
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/report-categories`);
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddNew = async () => {
    if (!newForm.name.trim()) {
      setError("Category name is required.");
      return;
    }
    setError("");
    setSavingNew(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/report-categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newForm.name.trim(), description: newForm.description.trim() || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || "Failed to create category.");
        return;
      }
      await fetchCategories();
      setNewForm({ name: "", description: "" });
      setAddingNew(false);
    } finally {
      setSavingNew(false);
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setEditForm({ name: cat.name, description: cat.description || "" });
    setError("");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({ name: "", description: "" });
    setError("");
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      setError("Category name is required.");
      return;
    }
    setError("");
    setSavingEdit(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/report-categories/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editForm.name.trim(), description: editForm.description.trim() || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.detail || "Failed to update category.");
        return;
      }
      await fetchCategories();
      cancelEdit();
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${BACKEND_URL}/api/report-categories/${id}`, { method: "DELETE" });
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>
            Report Categories
          </h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {categories.length} categor{categories.length !== 1 ? "ies" : "y"} configured
          </p>
        </div>
        {!addingNew && (
          <button
            onClick={() => { setAddingNew(true); setError(""); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
            data-testid="add-category-btn"
          >
            <Plus size={16} weight="bold" />
            Add Category
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p
          className="text-xs px-3 py-2 rounded-lg mb-4"
          style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}
          data-testid="category-error"
        >
          {error}
        </p>
      )}

      {/* Add New Row */}
      {addingNew && (
        <div
          className="flex items-start gap-3 p-4 rounded-xl border mb-4"
          style={{ backgroundColor: "rgba(26,92,55,0.04)", borderColor: "rgba(26,92,55,0.2)" }}
          data-testid="add-category-row"
        >
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5 uppercase tracking-wider" style={{ color: "#4A5450" }}>
                Name <span style={{ color: "#B85C38" }}>*</span>
              </label>
              <input
                autoFocus
                type="text"
                placeholder="e.g. Climate Change"
                value={newForm.name}
                onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleAddNew()}
                className={inputCls}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                data-testid="new-category-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 uppercase tracking-wider" style={{ color: "#4A5450" }}>
                Description <span className="opacity-50">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="Brief description"
                value={newForm.description}
                onChange={(e) => setNewForm((f) => ({ ...f, description: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleAddNew()}
                className={inputCls}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                data-testid="new-category-description-input"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6 flex-shrink-0">
            <button
              onClick={handleAddNew}
              disabled={savingNew}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
              data-testid="save-category-btn"
            >
              <Check size={15} weight="bold" />
              {savingNew ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => { setAddingNew(false); setNewForm({ name: "", description: "" }); setError(""); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors"
              style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}
            >
              <X size={15} weight="bold" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div
            className="w-6 h-6 border-2 rounded-full animate-spin"
            style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }}
          />
        </div>
      ) : categories.length === 0 && !addingNew ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: "rgba(26,92,55,0.08)" }}
          >
            <Tag size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No categories yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>
            Click &quot;Add Category&quot; to create your first report category.
          </p>
        </div>
      ) : (
        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: "rgba(26,92,55,0.1)" }}
        >
          {/* Table Header */}
          <div
            className="grid grid-cols-12 gap-4 px-5 py-3 text-xs font-medium uppercase tracking-wider border-b"
            style={{ backgroundColor: "rgba(26,92,55,0.04)", borderColor: "rgba(26,92,55,0.1)", color: "#4A5450" }}
          >
            <div className="col-span-4">Name</div>
            <div className="col-span-6">Description</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {categories.map((cat, idx) => (
            <div
              key={cat.id}
              className="grid grid-cols-12 gap-4 px-5 py-4 items-center border-b"
              style={{
                borderColor: "rgba(26,92,55,0.07)",
                backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAF8",
              }}
              data-testid={`category-row-${idx}`}
            >
              {editId === cat.id ? (
                /* Edit mode */
                <>
                  <div className="col-span-4">
                    <input
                      autoFocus
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      className={inputCls}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                      onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                      data-testid="edit-category-name-input"
                    />
                  </div>
                  <div className="col-span-6">
                    <input
                      type="text"
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Optional description"
                      className={inputCls}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                      onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                      data-testid="edit-category-description-input"
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    <button
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: "#1A5C37" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      data-testid="save-edit-btn"
                    >
                      <Check size={16} weight="bold" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: "#4A5450" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <X size={16} weight="bold" />
                    </button>
                  </div>
                </>
              ) : (
                /* View mode */
                <>
                  <div className="col-span-4 flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(26,92,55,0.08)" }}
                    >
                      <Tag size={14} weight="duotone" style={{ color: "#1A5C37" }} />
                    </div>
                    <span className="text-sm font-medium truncate" style={{ color: "#1C211F" }}>
                      {cat.name}
                    </span>
                  </div>
                  <div className="col-span-6">
                    <span className="text-sm" style={{ color: "#4A5450" }}>
                      {cat.description || <span className="opacity-40 italic">No description</span>}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => startEdit(cat)}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: "#1A5C37" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      data-testid={`edit-category-${idx}`}
                    >
                      <PencilSimple size={15} weight="duotone" />
                    </button>
                    <button
                      onClick={() => handleDelete(cat.id)}
                      disabled={deletingId === cat.id}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: "#B85C38" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(184,92,56,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      data-testid={`delete-category-${idx}`}
                    >
                      <Trash size={15} weight="duotone" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
